import { ref, unref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { panelizationPropositionCalculation } from '@/composables/panelizationPropositionCalculation';
import { usePanelEstimationResults } from '@/composables/usePanelEstimationResults';
import { useCarbonImpactTotals } from '@/composables/useCarbonImpactTotals';

export function useProjectSave() {
  const store = useStore();
  const router = useRouter();
  const { savePanelEstimationResults } = usePanelEstimationResults();
  const { buildCarbonImpactPayload } = useCarbonImpactTotals({ store });

  /**
   * Runs panelization calculation from selected walls + config, then saves both
   * panel estimation and panel count estimator data.
   *
   * @param {Ref<Array>|ComputedRef<Array>} wallsRef  reactive list of wall objects (full format with `parameters`)
   * @param {Object} panelConfig  optional overrides; all dimensions accept refs or plain numbers
   */
  async function runPanelizationAndSave(wallsRef, panelConfig = {}) {
    // explicit override (e.g. user-selected wall in PrefabWallViewer) takes precedence over auto-first
    const prefabricatedWallType =
      panelConfig.prefabricatedWallType !== undefined
        ? (unref(panelConfig.prefabricatedWallType) ?? null)
        : ((unref(wallsRef))?.[0]?.type ?? null);

    // If no thickness override is provided, derive it from the selected prefab wall schedule.
    let resolvedMaxPanelThickness = panelConfig.maxPanelThickness;
    if (resolvedMaxPanelThickness == null && prefabricatedWallType) {
      if (Object.keys(store.state.scheduleTable?.walls ?? {}).length < 1) {
        await store.dispatch('fetchScheduleTableData');
      }

      const selectedWallRows = store.state.scheduleTable?.walls?.[prefabricatedWallType];
      if (Array.isArray(selectedWallRows) && selectedWallRows.length > 0) {
        resolvedMaxPanelThickness = selectedWallRows.reduce((acc, material) => {
          return acc + Number(material?.material_thickness_m ?? 0);
        }, 0) * 12;
      }
    }

    const truckLength = panelConfig.truckLength ?? ref(576);
    const truckWidth = panelConfig.truckWidth ?? ref(110);
    const truckHeight = panelConfig.truckHeight ?? ref(110);
    const spacerThickness = panelConfig.spacerThickness ?? ref(0.25);
    const maxPanelLength = panelConfig.maxPanelLength ?? ref(110);
    const maxPanelWidth = panelConfig.maxPanelWidth ?? ref(288);
    const maxPanelThickness = resolvedMaxPanelThickness ?? ref(12);

    const thickness = Number(unref(spacerThickness)) + Number(unref(maxPanelThickness));

    const { createWallTypes, getTwoDigitNumbers, groupPanels, groupByTruck, createPanels } =
      panelizationPropositionCalculation({
        debouncedTruckLength: truckLength,
        debouncedTruckWidth: truckWidth,
        debouncedTruckHeight: truckHeight,
        thickness,
      });

    const wallTypes = createWallTypes(wallsRef, maxPanelLength);
    const pannelPortions = ref(getTwoDigitNumbers(wallTypes));
    const shipmentTrucks = ref(
      groupByTruck(groupPanels(createPanels(wallTypes, pannelPortions, unref(maxPanelLength), unref(maxPanelWidth))))
    );

    await savePanelEstimationResults({
      wallTypes,
      pannelPortions,
      shipmentTrucks,
      debouncedTruckLength: truckLength,
      debouncedTruckWidth: truckWidth,
      debouncedTruckHeight: truckHeight,
      debouncedSpacerThickness: spacerThickness,
    });

    if (prefabricatedWallType) {
      store.commit('setScheduleSelectedWallType', prefabricatedWallType);
    }

    const panelCountData = {
      panelConfiguration: {
        maxPanelWidth: Number(unref(maxPanelWidth)),
        maxPanelLength: Number(unref(maxPanelLength)),
      },
      selectedWalls: (unref(wallsRef) ?? []).map((wall) => ({
        wallType: wall.type ?? null,
        width: wall.parameters?.[0]?.width ?? null,
        totalArea: wall.parameters?.reduce((sum, p) => sum + (p.area ?? 0), 0) ?? null,
        totalLength: wall.parameters?.reduce((sum, p) => sum + (p.curveLength ?? 0), 0) ?? null,
      })),
      prefabricatedWallType,
    };
    await store.dispatch('savePanelCountEstimatorData', panelCountData);
  }

  async function initializeCarbonImpact() {
    if (Object.keys(store.state.scheduleTable?.walls ?? {}).length < 1) {
      await store.dispatch('fetchScheduleTableData');
    }

    const selectedWalls = store.state.projectData?.panelCountEstimator?.selectedWalls ?? [];
    const selectedWallsTotalArea = selectedWalls.reduce((sum, wall) => {
      return sum + Number(wall?.totalArea ?? 0);
    }, 0);

    const selectedWallType =
      store.state.projectData?.panelCountEstimator?.prefabricatedWallType ??
      store.state.scheduleTable?.selectedWallType;

    const availableWallTypes = Object.keys(store.state.scheduleTable?.walls ?? {});
    const effectiveSelectedWallType =
      availableWallTypes.find((wallType) => wallType === selectedWallType) ??
      availableWallTypes[0] ??
      null;

    if (effectiveSelectedWallType) {
      store.commit('setScheduleSelectedWallType', effectiveSelectedWallType);
    }

    const carbonImpactData = buildCarbonImpactPayload({
      selectedWallType: effectiveSelectedWallType,
      totalArea: selectedWallsTotalArea,
    });
    await store.dispatch('saveCarbonImpactData', carbonImpactData);
  }

  function navigateToDashboard() {
    const userRole = store.state.userRole || store.state.user?.role;
    const dashboardMap = {
      architect: 'ArchitectDashboard',
      structure: 'StructureDashboard',
    };
    router.push({ name: dashboardMap[userRole] || 'ArchitectDashboard' });
  }

  return { runPanelizationAndSave, initializeCarbonImpact, navigateToDashboard };
}
